import {
  alpha,
  Box,
  Card,
  Chip,
  Grid,
  makeStyles,
  useTheme,
} from '@material-ui/core';
import { ArrowForwardIos, Lock } from '@material-ui/icons';
import clsx from 'clsx';
import dayjs from 'dayjs';

import TagsV2 from 'components/TagsV2/TagsV2';
import useCurrentUser from 'hooks/useCurrentUser';
import {
  PosicaoAtendimentoEnum,
  type AtendimentoInterfaceV2,
} from 'store/reducers/atendimentos';
import getIconTipoCanal from 'utils/getIconTipoCanal';
import { normalizeDuration } from 'utils/normalizeDuration';

import DetailItem from './DetailItems';

const useStyles = makeStyles(({ palette, spacing }) => ({
  card: {
    width: '100%',
    padding: spacing(1),
    border: `1px solid transparent`,
    backgroundColor: alpha(palette.action.disabledBackground, 0.05),
    transition: 'all 0.1s ease',
    '&:hover': {
      backgroundColor: alpha(palette.primary.main, 0.1),
      border: `1px solid ${alpha(palette.primary.main, 0.2)}`,
      cursor: 'pointer',
    },
  },

  noHover: {
    '&:hover': {
      backgroundColor: alpha(palette.action.disabledBackground, 0.05),
      border: `1px solid transparent`,
      cursor: 'default',
    },
  },

  icon: {
    width: 16,
    height: 16,
    fill: palette.primary.main,
  },

  contentColumn: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    gap: 1,
  },

  rightColumn: {
    paddingTop: spacing(3),
  },

  rightColumnItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
  },

  arrowWrapper: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },

  tagsClickStop: {
    cursor: 'default',
  },

  tagsWrapper: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    marginTop: 4,
  },
}));

type FinalizadoPorType = 'ADMINISTRADOR' | 'ATENDENTE' | 'BOT' | 'CONTATO';

interface AtendimentoCardProps {
  atendimento: AtendimentoInterfaceV2;
  isAtendimentoEmAndamento?: boolean;
  onClick: () => void;
}

function StatusChip({ atual = false }: { atual?: boolean }) {
  const theme = useTheme();
  const bg = alpha(
    theme.palette.success.main,
    theme.palette.type === 'dark' ? 0.4 : 0.1,
  );

  if (atual) {
    return (
      <Chip
        label="ATUAL"
        style={{
          backgroundColor: bg,
          color: theme.palette.success.main,
          fontWeight: 600,
        }}
        size="small"
      />
    );
  }

  return (
    <Chip
      icon={
        <Lock
          style={{ width: 16, height: 16, fill: theme.palette.primary.main }}
        />
      }
      label="PRIVADO"
      style={{
        backgroundColor: bg,
        color: theme.palette.primary.main,
        fontSize: 12,
        fontWeight: 600,
        padding: 8,
      }}
      size="small"
    />
  );
}

export default function AtendimentoCard({
  atendimento,
  isAtendimentoEmAndamento = false,
  onClick,
}: AtendimentoCardProps) {
  const classes = useStyles();
  const currentUser = useCurrentUser();

  const estaEmAtendimento =
    atendimento.position === PosicaoAtendimentoEnum.ATENDENTE;

  const isMyCurrentAtendimento =
    atendimento.attendantCurrent?.id === currentUser?.id && estaEmAtendimento;

  const { hasAccess } = atendimento;

  const duracao = normalizeDuration(atendimento.durationInSeconds);

  const getFinalizadorInfo = (
    finalizadoPor: FinalizadoPorType,
    atendimento: AtendimentoInterfaceV2,
  ): { label: string; value: string } => {
    const label = 'Finalizado por';

    const finalizadorMap = {
      ADMINISTRADOR: {
        label,
        value: atendimento.attendantFinisher?.name || 'Administrador',
      },
      ATENDENTE: {
        label,
        value: atendimento.attendantFinisher?.name || 'Atendente',
      },
      BOT: {
        label,
        value: atendimento.botFinisher?.name || 'Bot',
      },
      CONTATO: {
        label,
        value: atendimento.contact?.name || 'Contato',
      },
    };

    return finalizadorMap[finalizadoPor] || { label, value: ' - ' };
  };

  const finalizador = getFinalizadorInfo(
    atendimento.finalizadoPor as FinalizadoPorType,
    atendimento,
  );

  const contentOpacity = !hasAccess ? 0.5 : 1;
  const contentFilter = !hasAccess ? 'grayscale(100%)' : 'none';

  const renderCampoPosicao = (position: PosicaoAtendimentoEnum) => {
    if (position === PosicaoAtendimentoEnum.ATENDENTE) return null;

    const value = {
      [PosicaoAtendimentoEnum.API_DISPARO]: 'API Disparo',
      [PosicaoAtendimentoEnum.CAMPANHA_ENVIADA]: 'Campanha Enviada',
      [PosicaoAtendimentoEnum.BOT]: 'Bot',
      [PosicaoAtendimentoEnum.ATENDENTE]: 'Atendente',
      [PosicaoAtendimentoEnum.AGENTE_IA]: 'Agente IA',
      [PosicaoAtendimentoEnum.EQUIPE]: 'Fila de espera',
      [PosicaoAtendimentoEnum.AVALIACAO_AT]: 'Avaliação',
    };

    return (
      <DetailItem
        ellipsis
        isContactItem
        layout="row"
        label="Posição"
        value={value[position]}
      />
    );
  };

  return (
    <Card
      key={atendimento.id}
      variant="outlined"
      elevation={0}
      className={clsx(classes.card, {
        [classes.noHover]: !hasAccess,
      })}
      onClick={!hasAccess ? undefined : onClick}
    >
      <Grid container>
        <Grid
          item
          xs={7}
          className={classes.contentColumn}
          style={{
            opacity: contentOpacity,
            filter: contentFilter,
          }}
        >
          <DetailItem
            ellipsis
            isContactItem
            layout="row"
            label="Código"
            value={`${atendimento.code}`}
          />

          {isAtendimentoEmAndamento ? (
            <DetailItem
              ellipsis
              isContactItem
              layout="row"
              label="Iniciado"
              value={dayjs(atendimento.startedIn).format(
                'DD/MM/YY[, às] HH:mm',
              )}
            />
          ) : (
            <DetailItem
              ellipsis
              isContactItem
              layout="row"
              label="Finalizado"
              value={dayjs(atendimento.finishedIn).format(
                'DD/MM/YY[, às] HH:mm',
              )}
            />
          )}

          {estaEmAtendimento && isAtendimentoEmAndamento && (
            <DetailItem
              ellipsis
              isContactItem
              layout="row"
              label="Atendente"
              value={atendimento.attendantCurrent?.name}
            />
          )}

          {!isAtendimentoEmAndamento && (
            <DetailItem
              ellipsis
              isContactItem
              layout="row"
              label={finalizador.label}
              value={finalizador.value}
            />
          )}

          {isAtendimentoEmAndamento && renderCampoPosicao(atendimento.position)}

          <DetailItem
            ellipsis
            isContactItem
            layout="row"
            label="Canal"
            value={atendimento.channel?.name || 'Canal desconhecido'}
            startIcon={
              atendimento.channel?.type
                ? getIconTipoCanal({
                    tipoCanal: atendimento.channel.type,
                    size: 16,
                    isModoHibrido: atendimento.channel.isHybridMode,
                  })
                : null
            }
          />
        </Grid>

        <Grid
          item
          xs={4}
          className={classes.rightColumn}
          style={{
            opacity: contentOpacity,
            filter: contentFilter,
          }}
        >
          <DetailItem
            ellipsis
            isContactItem
            layout="row"
            label="Duração"
            value={duracao || ' - '}
          />
          <DetailItem
            ellipsis
            isContactItem
            layout="row"
            label="Equipe"
            value={atendimento.team?.name || ' - '}
          />
        </Grid>

        <Grid item xs={1} className={classes.rightColumnItem}>
          {!hasAccess && <StatusChip />}
          {isMyCurrentAtendimento && isAtendimentoEmAndamento && (
            <StatusChip atual />
          )}

          {hasAccess && (
            <Box className={classes.arrowWrapper}>
              <ArrowForwardIos fontSize="small" color="action" />
            </Box>
          )}
        </Grid>

        <Box
          onClick={e => e.stopPropagation()}
          className={classes.tagsClickStop}
        >
          <DetailItem
            isContactItem
            layout="row"
            label="Tags"
            fullWidth
            className={classes.tagsWrapper}
            value={
              <TagsV2
                tags={
                  atendimento.tags?.map(tag => ({
                    id: tag.id,
                    nome: tag.name,
                    cor: tag.color,
                  })) ?? []
                }
                id={atendimento.id}
              />
            }
          />
        </Box>
      </Grid>
    </Card>
  );
}
